import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  LoadLaneModel,
  LoadLaneMovementModel,
  LoadLaneVehicleModel,
} from '@/types';
import { DUE_SOON_THRESHOLD_MINUTES, LoadLaneStatus } from './constants';

dayjs.extend(utc);

export type UpcomingDayFilter = 'today' | 'tomorrow' | { date: string };

export type LaneDisplayRow = {
  key: string;
  locationLane: string;
  movementID?: number;
  movementRef?: string;
  etaDate?: string;
  collectionDate?: string;
  providerName?: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  completedAt?: string;
  movementTotalStockCount: number;
  currentVehicleCount: number;
  jobChainId?: number | null;
  vehicles: LoadLaneVehicleModel[];
  laneLoadIndex: number;
  laneLoadCount: number;
};

export type FooterLaneChipRow = {
  locationLane: string;
  status: LoadLaneStatus;
  hasAddedAfterCompleted: boolean;
  hasRemovedAfterCompleted: boolean;
  currentVehicleCount: number;
  movementTotalStockCount: number;
  etaDate?: string;
  lastPulledAt?: string;
  movementID?: number;
};

const LOAD_LANE_STATUS_RANK: Record<LoadLaneStatus, number> = {
  overdue: 0,
  dueSoon: 1,
  onTime: 2,
  complete: 3,
  vacant: 4,
};

const vacantLaneRow = (locationLane: string): LaneDisplayRow => ({
  key: locationLane,
  locationLane,
  movementTotalStockCount: 0,
  currentVehicleCount: 0,
  vehicles: [],
  laneLoadIndex: 0,
  laneLoadCount: 0,
});

/** API returns etaDate in UTC; convert to local time for display, filtering, and diffs. */
export const parseEtaDateLocal = (etaDate?: string): Dayjs | null => {
  if (!etaDate) return null;
  return dayjs.utc(etaDate).local();
};

const resolveFilterDay = (dayFilter: UpcomingDayFilter) => {
  if (dayFilter === 'tomorrow') return dayjs().add(1, 'day').startOf('day');
  if (dayFilter === 'today') return dayjs().startOf('day');

  const parsed = dayjs(dayFilter.date);
  return parsed.isValid() ? parsed.startOf('day') : dayjs().startOf('day');
};

const matchesLocalDay = (
  etaDate: string | undefined,
  dayFilter: UpcomingDayFilter,
) => {
  const eta = parseEtaDateLocal(etaDate);
  if (!eta) return false;
  return eta.isSame(resolveFilterDay(dayFilter), 'day');
};

const compareAssignedLoads = (
  left: LoadLaneMovementModel,
  right: LoadLaneMovementModel,
) => {
  const leftEta =
    parseEtaDateLocal(left.etaDate)?.valueOf() ?? Number.MAX_SAFE_INTEGER;
  const rightEta =
    parseEtaDateLocal(right.etaDate)?.valueOf() ?? Number.MAX_SAFE_INTEGER;
  if (leftEta !== rightEta) return leftEta - rightEta;
  return left.movementID - right.movementID;
};

export const getLoadLaneStatus = (lane: LaneDisplayRow): LoadLaneStatus => {
  if (!lane.movementID) return 'vacant';
  if (!lane.etaDate) return 'onTime';

  const eta = parseEtaDateLocal(lane.etaDate)!;
  const now = dayjs();

  if (
    lane.movementTotalStockCount > 0 &&
    lane.currentVehicleCount >= lane.movementTotalStockCount &&
    lane.completedAt &&
    dayjs(lane.completedAt).isBefore(eta)
  )
    return 'complete';

  if (eta.isBefore(now)) return 'overdue';

  const minutesUntilEta = eta.diff(now, 'minute');
  if (minutesUntilEta <= DUE_SOON_THRESHOLD_MINUTES) return 'dueSoon';

  return 'onTime';
};

export const formatDueAt = (etaDate?: string) => {
  const eta = parseEtaDateLocal(etaDate);
  if (!eta) return '—';

  const time = eta.format('HH:mm');
  const dayDiff = eta.startOf('day').diff(dayjs().startOf('day'), 'day');

  if (dayDiff === 0) return time;
  if (dayDiff === -1) return `Yesterday ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  if (dayDiff > 1 && dayDiff <= 7) return `${eta.format('ddd')} ${time}`;
  return `${eta.format('M/D')} ${time}`;
};

export const formatOverdueDuration = (
  completedAt?: string,
  etaDate?: string,
) => {
  if (!etaDate) return '—';

  if (completedAt && dayjs(completedAt).isBefore(etaDate)) return '—';

  const eta = parseEtaDateLocal(etaDate);
  const now = dayjs();
  if (!eta || !eta.isBefore(now)) return '—';

  const totalMinutes = now.diff(eta, 'minute');
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) return `+${hours}h ${minutes}m`;
  return `+${minutes}m`;
};

export const buildLaneTableRows = (data: LoadLaneModel): LaneDisplayRow[] => {
  const movementsByLane = new Map<string, LoadLaneMovementModel[]>();

  data.movements.forEach((movement) => {
    const lane = movement.locationLane?.trim();
    if (!lane) return;

    const assigned = movementsByLane.get(lane) ?? [];
    assigned.push(movement);
    movementsByLane.set(lane, assigned);
  });

  return getLaneNumbers(data).flatMap((lane) => {
    const assigned = (movementsByLane.get(lane) ?? [])
      .slice()
      .sort(compareAssignedLoads);
    if (assigned.length === 0) return [vacantLaneRow(lane)];

    return assigned.map((movement, index) => ({
      ...movement,
      key: `${lane}-${movement.movementID}`,
      locationLane: lane,
      laneLoadIndex: index + 1,
      laneLoadCount: assigned.length,
    }));
  });
};

export const getUpcomingLoads = (
  data: LoadLaneModel,
  dayFilter: UpcomingDayFilter,
): LoadLaneMovementModel[] =>
  data.movements.filter(
    (movement) =>
      !movement.locationLane?.trim() &&
      movement.etaDate &&
      matchesLocalDay(movement.etaDate, dayFilter),
  );

/** All known lanes: configured location lanes plus any lane that already has a load. */
export const getLaneNumbers = (data: LoadLaneModel) => {
  const lanes: string[] = [];
  const seen = new Set<string>();

  const addLane = (value?: string) => {
    const lane = value?.trim();
    if (!lane || seen.has(lane)) return;
    seen.add(lane);
    lanes.push(lane);
  };

  data.locationLanes.forEach(addLane);
  data.movements.forEach((movement) => addLane(movement.locationLane));
  return lanes;
};

export const getVacantLaneNumbers = (data: LoadLaneModel) => {
  const occupiedLanes = new Set(
    data.movements
      .map((movement) => movement.locationLane?.trim())
      .filter((lane): lane is string => !!lane),
  );

  return getLaneNumbers(data).filter((lane) => !occupiedLanes.has(lane));
};

export const getAssignableLaneNumbers = (data: LoadLaneModel) =>
  getLaneNumbers(data).sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true }),
  );

export const getVehicleDescription = (vehicle: LoadLaneVehicleModel) =>
  [vehicle.colourDesc, vehicle.productDesc].filter(Boolean).join(' ');

export const getStatusCounts = (lanes: LaneDisplayRow[]) =>
  lanes.reduce(
    (acc, lane) => {
      acc[getLoadLaneStatus(lane)] += 1;
      return acc;
    },
    {
      overdue: 0,
      dueSoon: 0,
      onTime: 0,
      complete: 0,
      vacant: 0,
    },
  );

export const getFooterStats = (lanes: LaneDisplayRow[]) => ({
  laneCount: new Set(lanes.map((lane) => lane.locationLane)).size,
  pulledVehicles: lanes.reduce(
    (sum, lane) => sum + lane.currentVehicleCount,
    0,
  ),
  plannedVehicles: lanes.reduce(
    (sum, lane) => sum + lane.movementTotalStockCount,
    0,
  ),
});

export const getLaneVehicleChangeFlags = (lane: LaneDisplayRow) => ({
  hasAddedAfterCompleted: lane.vehicles.some(
    (vehicle) => vehicle.addedAfterCompleted,
  ),
  hasRemovedAfterCompleted: lane.vehicles.some(
    (vehicle) => vehicle.removedAfterCompleted,
  ),
});

export const getLaneLastPulledAt = (lane: LaneDisplayRow) => {
  const pulledDates = lane.vehicles
    .map((vehicle) => vehicle.pulledDate)
    .filter((date): date is string => Boolean(date));

  if (!pulledDates.length) return undefined;

  return pulledDates.reduce((latest, date) =>
    dayjs.utc(date).isAfter(dayjs.utc(latest)) ? date : latest,
  );
};

export const getFooterLaneChips = (
  lanes: LaneDisplayRow[],
): FooterLaneChipRow[] => {
  const rowsByLane = new Map<string, LaneDisplayRow[]>();
  lanes.forEach((lane) => {
    const rows = rowsByLane.get(lane.locationLane) ?? [];
    rows.push(lane);
    rowsByLane.set(lane.locationLane, rows);
  });

  return [...rowsByLane.entries()].map(([locationLane, rows]) => {
    const representative = rows.reduce((current, row) =>
      LOAD_LANE_STATUS_RANK[getLoadLaneStatus(row)] <
      LOAD_LANE_STATUS_RANK[getLoadLaneStatus(current)]
        ? row
        : current,
    );
    const flags = rows.reduce(
      (acc, row) => {
        const next = getLaneVehicleChangeFlags(row);
        return {
          hasAddedAfterCompleted:
            acc.hasAddedAfterCompleted || next.hasAddedAfterCompleted,
          hasRemovedAfterCompleted:
            acc.hasRemovedAfterCompleted || next.hasRemovedAfterCompleted,
        };
      },
      { hasAddedAfterCompleted: false, hasRemovedAfterCompleted: false },
    );
    const pulledDates = rows
      .map((row) => getLaneLastPulledAt(row))
      .filter((date): date is string => Boolean(date));
    const lastPulledAt = pulledDates.length
      ? pulledDates.reduce((latest, date) =>
          dayjs.utc(date).isAfter(dayjs.utc(latest)) ? date : latest,
        )
      : undefined;

    return {
      locationLane,
      status: getLoadLaneStatus(representative),
      ...flags,
      currentVehicleCount: rows.reduce(
        (sum, row) => sum + row.currentVehicleCount,
        0,
      ),
      movementTotalStockCount: rows.reduce(
        (sum, row) => sum + row.movementTotalStockCount,
        0,
      ),
      etaDate: representative.etaDate,
      lastPulledAt,
      movementID: representative.movementID,
    };
  });
};

export const getProviderInitials = (providerName?: string) => {
  if (!providerName) return '—';
  const words = providerName.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

export const formatLaneNumber = (lane: string) => {
  const numeric = parseInt(lane, 10);
  if (!Number.isNaN(numeric)) {
    return numeric.toString().padStart(2, '0');
  }
  return lane;
};
